import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { isDefined } from 'remeda';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

export type LoadFieldCatalogueVariables = { viewType: string };

const FieldDescriptorSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.string(),
  displayable: z.boolean(),
});

const FieldCatalogueSchema = z
  .object({ data: z.array(FieldDescriptorSchema) })
  .transform(response => response.data);

export type FieldDescriptor = z.infer<typeof FieldDescriptorSchema>;
export type FieldCatalogue = z.infer<typeof FieldCatalogueSchema>;

async function loadFieldCatalogueQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { viewType }: LoadFieldCatalogueVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`field_catalogues/${viewType}`);

  const parseResult = FieldCatalogueSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'fieldCatalogue');
    throw new SilentError();
  }

  return parseResult.data;
}

const emptyFieldCatalogue: FieldCatalogue = [];

export function useLoadFieldCatalogue(
  variables: LoadFieldCatalogueVariables,
  options: QueryOptions<FieldCatalogue> = {},
) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<FieldCatalogue, ApiError>({
    queryKey: ['fieldCatalogue', variables],
    queryFn: () => loadFieldCatalogueQueryFn(axiosInstance, variables),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyFieldCatalogue;
    },
    ...options,
  });

  return addResourceNameToQueryResult<
    FieldCatalogue,
    ApiError,
    'fieldCatalogue',
    'PreventUndefined'
  >('fieldCatalogue', queryResult, emptyFieldCatalogue);
}
